import ChatBubble from '../ui/ChatBubble';
import Waveform from '../ui/Waveform';
import CertificateMini from '../ui/CertificateMini';
import ImageSlot from '../ui/ImageSlot';

/**
 * Feature bento — the "Inside Lincoln" mosaic on the homepage.
 * 12-column CSS grid with mixed cell heights. Each tile shows one feature:
 * voice tutor, quizzes, certificates, flashcards, multilingual, voice
 * calling, worksheets/podcasts/video, suggested questions.
 */
export default function FeatureBento() {
  return (
    <section className="lc-section" style={{ background: '#fff' }}>
      <div className="lc-container">
        <div className="text-center" style={{ marginBottom: 56 }}>
          <span className="lc-eyebrow">Inside Lincoln</span>
          <h2 className="mt-3.5">Learn the way that works for you.</h2>
          <p className="mx-auto mt-3.5" style={{ color: 'var(--muted)', fontSize: 18, maxWidth: 580 }}>
            Voice, quizzes, flashcards, certificates, live tutors — every way to learn, woven into one seamless experience.
          </p>
        </div>

        <div className="grid" style={{ gridTemplateColumns: 'repeat(12, 1fr)', gridAutoRows: 'minmax(180px, auto)', gap: 18 }}>
          {/* HERO — Voice tutor (wide) */}
          <div className="relative overflow-hidden text-white"
            style={{ gridColumn: 'span 7', gridRow: 'span 2', background: 'linear-gradient(135deg, #0b1f38 0%, #1a3a62 100%)', borderRadius: 24, padding: 36 }}>
            <span className="lc-tag" style={{ background: 'rgba(240,184,12,0.2)', color: 'var(--yellow-2)' }}>VOICE-FIRST</span>
            <h3 className="text-white" style={{ marginTop: 16, fontSize: 32, maxWidth: 360 }}>Talk to Lincoln. He talks back.</h3>
            <p className="mt-3" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, maxWidth: 360 }}>
              Natural conversations — interrupt him, ask follow-ups, switch languages mid-sentence.
            </p>
            <div className="flex flex-col gap-2.5 mt-5" style={{ maxWidth: 360 }}>
              <ChatBubble side="user" text="Wait — what's the chain rule again?" />
              <ChatBubble side="lincoln" text="Of course! Imagine two functions stacked..." typing />
            </div>
            <div className="absolute" style={{ bottom: 24, right: 24, color: 'var(--yellow)', opacity: 0.5 }}>
              <Waveform bars={12} large />
            </div>
          </div>

          {/* Quizzes */}
          <div className="flex flex-col justify-between"
            style={{ gridColumn: 'span 5', background: 'var(--bg-cream)', borderRadius: 24, padding: 28 }}>
            <div>
              <span className="lc-tag yellow">AUTO-QUIZZES</span>
              <h3 className="mt-3" style={{ fontSize: 22 }}>Quizzes built for you, instantly.</h3>
              <p className="mt-2" style={{ color: 'var(--muted)', fontSize: 14 }}>
                Lincoln writes a quiz from any material — graded, with explanations.
              </p>
            </div>
            <div className="bg-white shadow-sm mt-4 rounded-xl" style={{ padding: 14 }}>
              <div className="mb-2" style={{ fontSize: 12, color: 'var(--muted)' }}>Question 3 of 8</div>
              <div className="font-semibold mb-2.5" style={{ fontSize: 13 }}>Which gas do plants release during photosynthesis?</div>
              <div className="flex flex-col gap-1.5">
                {['CO₂', 'Oxygen', 'Nitrogen'].map((a, i) => (
                  <div
                    key={a}
                    className="flex justify-between items-center"
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      fontSize: 12,
                      border: `1.5px solid ${i === 1 ? 'var(--green)' : 'var(--line)'}`,
                      background: i === 1 ? 'rgba(34,181,114,0.1)' : '#fff',
                      color: i === 1 ? 'var(--green-2)' : 'var(--ink-2)',
                      fontWeight: i === 1 ? 600 : 500,
                    }}
                  >
                    <span>{a}</span>
                    {i === 1 && <span>✓</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Certificates with QR */}
          <div className="flex flex-col justify-between"
            style={{ gridColumn: 'span 4', background: '#fff', border: '1px solid var(--line)', borderRadius: 24, padding: 28 }}>
            <div>
              <span className="lc-tag green">CERTIFICATES + QR</span>
              <h3 className="mt-3" style={{ fontSize: 22 }}>Verifiable certificates.</h3>
              <p className="mt-2" style={{ color: 'var(--muted)', fontSize: 14 }}>
                Every certificate has a scannable QR for instant verification.
              </p>
            </div>
            <CertificateMini />
          </div>

          {/* Flashcards */}
          <div className="relative overflow-hidden flex flex-col"
            style={{ gridColumn: 'span 4', background: 'linear-gradient(135deg, #fff8e1, #ffefb1)', borderRadius: 24 }}>
            <div style={{ padding: '28px 28px 16px' }}>
              <span className="lc-tag" style={{ background: 'rgba(11,31,56,0.1)', color: 'var(--navy)' }}>FLASHCARDS</span>
              <h3 className="mt-3" style={{ fontSize: 22 }}>Spaced repetition that sticks.</h3>
              <p className="mt-2" style={{ color: 'var(--ink-2)', fontSize: 14 }}>Auto-generated flashcards from every lesson.</p>
            </div>
            <div className="relative flex-1" style={{ minHeight: 160, margin: '0 16px 16px' }}>
              <ImageSlot id="feat-flashcards" style={{ position: 'absolute', inset: 0 }} shape="rounded" radius="14"
                src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=900&auto=format&fit=crop&q=80"
                placeholder="Drop a photo: student studying with flashcards" />
              <div className="absolute font-semibold"
                style={{ bottom: 12, left: 12, background: '#fff', borderRadius: 8, padding: '8px 10px', boxShadow: '0 4px 14px rgba(0,0,0,0.15)', fontSize: 11, color: 'var(--ink)', border: '1px solid var(--line)', transform: 'rotate(-3deg)' }}>
                <div className="tracking-wider" style={{ fontSize: 8, color: 'var(--muted)' }}>FRONT</div>
                <div className="mt-0.5">What is photosynthesis?</div>
              </div>
            </div>
          </div>

          {/* Multilingual */}
          <div className="flex flex-col overflow-hidden"
            style={{ gridColumn: 'span 4', background: '#fff', border: '1px solid var(--line)', borderRadius: 24 }}>
            <div style={{ padding: '28px 28px 14px' }}>
              <span className="lc-tag blue">3 LANGUAGES</span>
              <h3 className="mt-3" style={{ fontSize: 22 }}>Speaks like a local.</h3>
              <p className="mt-2" style={{ color: 'var(--muted)', fontSize: 14 }}>
                From Finnish to Swahili — Lincoln teaches in your tongue.
              </p>
            </div>
            <div className="relative flex-1" style={{ minHeight: 120, margin: '0 16px 12px' }}>
              <ImageSlot id="feat-languages" style={{ position: 'absolute', inset: 0 }} shape="rounded" radius="14"
                src="https://images.unsplash.com/photo-1529390079861-591de354faf5?w=900&auto=format&fit=crop&q=80"
                placeholder="Drop a photo: students from different countries" />
            </div>
            <div className="flex flex-wrap" style={{ padding: '0 28px 24px', gap: 5 }}>
              {['🇫🇮 Suomi', '🇸🇪 Svenska', '🇲🇹 Malti', '🇪🇸 Español', '🇵🇰 اردو', '🇰🇪 Kiswahili'].map((l) => (
                <span key={l} className="rounded-full font-medium" style={{ fontSize: 11, padding: '4px 9px', background: 'var(--bg-soft)', color: 'var(--ink-2)' }}>{l}</span>
              ))}
              <span className="rounded-full font-semibold text-white" style={{ fontSize: 11, padding: '4px 9px', background: 'var(--navy)' }}>+34 more</span>
            </div>
          </div>

          {/* Voice calling */}
          <div className="relative overflow-hidden flex flex-col text-white"
            style={{ gridColumn: 'span 4', background: 'linear-gradient(135deg, #1e7a6a, #22b572)', borderRadius: 24 }}>
            <div style={{ padding: '28px 28px 14px' }}>
              <span className="lc-tag" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>VOICE CALLING</span>
              <h3 className="text-white mt-3" style={{ fontSize: 22 }}>Call Lincoln, anytime.</h3>
              <p className="mt-2" style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
                Talk to him on the phone like you would a tutor.
              </p>
            </div>
            <div className="relative flex-1" style={{ minHeight: 110, margin: '0 16px 12px' }}>
              <ImageSlot id="feat-voice-call" style={{ position: 'absolute', inset: 0 }} shape="rounded" radius="14"
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=900&auto=format&fit=crop&q=80"
                placeholder="Drop a photo: person on phone" />
            </div>
            <div className="flex items-center gap-2.5"
              style={{ background: 'rgba(0,0,0,0.18)', margin: '0 16px 16px', borderRadius: 12, padding: '10px 16px' }}>
              <div className="inline-flex items-center justify-center rounded-full flex-shrink-0" style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.2)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.71 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.58 2.81.71A2 2 0 0 1 22 16.92z" />
                </svg>
              </div>
              <div>
                <div className="font-mono font-bold" style={{ fontSize: 13 }}>+358 41 575 1803</div>
                <div className="opacity-85" style={{ fontSize: 10 }}>24/7 · 3 languages</div>
              </div>
            </div>
          </div>

          {/* Worksheets / Podcasts / Video */}
          <div className="overflow-hidden flex flex-col"
            style={{ gridColumn: 'span 7', background: '#fff', border: '1px solid var(--line)', borderRadius: 24 }}>
            <div style={{ padding: '28px 28px 16px' }}>
              <span className="lc-tag" style={{ background: 'rgba(122,58,138,0.12)', color: '#7a3a8a' }}>WORKSHEETS · PODCASTS · VIDEO</span>
              <h3 className="mt-3" style={{ fontSize: 26 }}>Every lesson comes with takeaways.</h3>
              <p className="mt-2" style={{ color: 'var(--muted)', fontSize: 14, maxWidth: 460 }}>
                Curated YouTube videos, AI-narrated podcasts, and printable worksheets — generated for each lesson, ready when you are.
              </p>
            </div>
            <div className="grid flex-1" style={{ padding: '0 16px 16px', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 12 }}>
              <div className="relative overflow-hidden" style={{ borderRadius: 14, background: 'var(--navy)', minHeight: 200 }}>
                <ImageSlot id="feat-yt-1" style={{ position: 'absolute', inset: 0 }} shape="rounded" radius="14"
                  src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=900&auto=format&fit=crop&q=80"
                  placeholder="Drop video thumbnail" />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.7) 100%)' }} />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="inline-flex items-center justify-center rounded-full"
                    style={{ width: 50, height: 50, background: 'rgba(255,0,0,0.92)', boxShadow: '0 6px 18px rgba(0,0,0,0.3)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><polygon points="6,4 20,12 6,20" /></svg>
                  </div>
                </div>
                <span className="absolute font-bold tracking-wider text-white" style={{ top: 10, left: 10, background: 'rgba(0,0,0,0.85)', padding: '3px 8px', borderRadius: 4, fontSize: 9, pointerEvents: 'none' }}>
                  ▶ YOUTUBE · 12 MIN
                </span>
                <div className="absolute font-semibold text-white pointer-events-none" style={{ bottom: 10, left: 10, right: 10, fontSize: 12 }}>
                  How neural networks really work
                </div>
              </div>

              <div className="relative overflow-hidden text-white flex flex-col justify-between"
                style={{ borderRadius: 14, background: 'linear-gradient(135deg, #7a3a8a, #5b3a8a)', padding: 14, minHeight: 200 }}>
                <span className="self-start font-bold tracking-wider" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', padding: '3px 8px', borderRadius: 4, fontSize: 9 }}>
                  🎙 PODCAST · 8 MIN
                </span>
                <div>
                  <div className="font-bold leading-tight" style={{ fontSize: 13 }}>Module 3 recap, narrated</div>
                  <Waveform bars={14} color="#fff" />
                  <div className="flex items-center gap-2 mt-2">
                    <button type="button" className="inline-flex items-center justify-center rounded-full" style={{ width: 32, height: 32, background: '#fff' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="#7a3a8a"><polygon points="6,4 20,12 6,20" /></svg>
                    </button>
                    <span className="opacity-85" style={{ fontSize: 10 }}>3:42 / 8:14</span>
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden flex flex-col justify-between"
                style={{ borderRadius: 14, background: 'var(--bg-cream)', border: '1px solid var(--line)', padding: 12, minHeight: 200 }}>
                <span className="self-start font-bold tracking-wider text-white" style={{ background: 'var(--green)', padding: '3px 8px', borderRadius: 4, fontSize: 9 }}>
                  📄 WORKSHEET · PDF
                </span>
                <div className="bg-white flex-1 mt-2 mb-2" style={{ borderRadius: 6, padding: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div className="font-bold mb-1.5" style={{ fontSize: 7, color: 'var(--navy)' }}>WORKSHEET 4 · BIAS IN AI</div>
                  {[100, 80, 60, 90, 70].map((w, i) => (
                    <div key={i} style={{ height: 3, background: 'var(--line-2)', borderRadius: 2, marginBottom: 4, width: `${w}%` }} />
                  ))}
                  <div className="font-semibold mt-1.5" style={{ padding: 4, background: 'rgba(240,184,12,0.15)', borderRadius: 3, fontSize: 7, color: 'var(--ink)' }}>
                    Q: Name 2 sources of bias…
                  </div>
                </div>
                <button type="button" className="self-start font-bold text-white" style={{ fontSize: 10, padding: '6px 10px', borderRadius: 6, background: 'var(--navy)' }}>
                  Download PDF
                </button>
              </div>
            </div>
          </div>

          {/* Suggested questions */}
          <div className="relative overflow-hidden flex flex-col"
            style={{ gridColumn: 'span 5', background: 'linear-gradient(135deg, #f0b80c 0%, #f5c63a 100%)', borderRadius: 24, padding: 28 }}>
            <div>
              <span className="lc-tag" style={{ background: 'rgba(11,31,56,0.12)', color: 'var(--navy)' }}>SUGGESTED QUESTIONS</span>
              <h3 className="mt-3" style={{ fontSize: 22, color: 'var(--navy)' }}>Stuck? Lincoln nudges you.</h3>
              <p className="mt-2" style={{ color: 'var(--ink-2)', fontSize: 14 }}>
                He suggests the next best question — so you never run out of curiosity.
              </p>
            </div>
            <div className="flex flex-col gap-2 flex-1 justify-end" style={{ marginTop: 20 }}>
              {[
                { icon: '💡', text: 'Want a real-world example?' },
                { icon: '🎯', text: 'Should I quiz you on this?' },
                { icon: '📚', text: 'Show me a worksheet on it' },
                { icon: '🌐', text: 'Explain it in another language?' },
              ].map((s, i) => (
                <button key={i} type="button"
                  className="text-left font-semibold flex items-center gap-2.5 transition-all"
                  style={{ background: 'rgba(11,31,56,0.08)', color: 'var(--navy)', border: '1px solid rgba(11,31,56,0.15)', padding: '10px 14px', borderRadius: 12, fontSize: 13 }}>
                  <span className="inline-flex items-center justify-center rounded-full flex-shrink-0" style={{ width: 24, height: 24, background: 'var(--navy)', color: 'var(--yellow)', fontSize: 12 }}>{s.icon}</span>
                  {s.text}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
